import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip } from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import CloseIcon from "@mui/icons-material/Close";

import useZoomRtms from "../hooks/useZoomRtms";
import { RTMS_STATUS } from "../services/zoomMeeting";
import CaptureBar, { ICON_BTN_SX, STOP_BTN_SX } from "./CaptureBar";
import StopCaptureDialog from "./StopCaptureDialog";

// The meeting bar (TT-1207 F2): inside a Zoom meeting, on the doc that
// meeting is streaming into, the RTMS stream's status, clock, pause/resume
// and stop (useZoomRtms). Stop ends the tapestry, so it asks once.

function statusLabel(status) {
  switch (status) {
    case RTMS_STATUS.CONNECTING:
      return "Connecting";
    case RTMS_STATUS.PAUSED:
      return "Paused";
    case RTMS_STATUS.STOPPED:
      return "Ended";
    default:
      // started, or a client too old to say
      return "Live";
  }
}

const ZoomRtmsBar = ({ docId }) => {
  const rtms = useZoomRtms(docId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const stop = useCallback(() => {
    setConfirmOpen(false);
    return rtms.stop();
  }, [rtms]);

  if (!rtms.active || dismissed) return null;

  const { status, busy } = rtms;
  const ended = status === RTMS_STATUS.STOPPED;

  return (
    <CaptureBar
      label={statusLabel(status)}
      quiet={status !== RTMS_STATUS.STARTED && status !== null}
      source={rtms.starter ? `Zoom · ${rtms.starter}` : "Zoom meeting"}
      elapsed={rtms.elapsed}
      error={rtms.error}
    >
      {rtms.pauseSupported && status === RTMS_STATUS.STARTED && (
        <Tooltip title="Pause the live transcript">
          <span>
            <IconButton size="small" sx={ICON_BTN_SX} onClick={rtms.pause} disabled={busy} aria-label="Pause">
              <PauseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {status === RTMS_STATUS.PAUSED && (
        <Tooltip title="Resume the live transcript">
          <span>
            <IconButton size="small" sx={ICON_BTN_SX} onClick={rtms.resume} disabled={busy} aria-label="Resume">
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!ended && (
        <Tooltip title="Stop and end this tapestry">
          <span>
            <IconButton
              size="small"
              sx={STOP_BTN_SX}
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
              aria-label="Stop the live transcript"
            >
              <StopCircleIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {ended && (
        <IconButton size="small" sx={ICON_BTN_SX} onClick={() => setDismissed(true)} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      <StopCaptureDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onStop={stop}
        resumable={rtms.pauseSupported}
      />
    </CaptureBar>
  );
};

ZoomRtmsBar.propTypes = {
  docId: PropTypes.string,
};

export default ZoomRtmsBar;
